import { z } from 'zod'
import { storefrontShopIds, storefrontShopSchema } from '../shopRegistry'
import {
  MenuPageBuilder,
  MenuPageBuilderButton,
  menuPageBuilderSchema,
  menuPageBuilderButtonSchema,
  sanitizeMenuPageBuilder,
  sanitizeMenuPageBuilderButton,
} from './menuPageBuilder'

export const landingPageManagedShopIds = storefrontShopIds
export const landingPageManagedShopSchema = storefrontShopSchema
export type LandingPageManagedShop = z.infer<typeof landingPageManagedShopSchema>
export type LandingPageManagedShopKey = `${LandingPageManagedShop}`

export const landingPageProductInquirySettingsSchema = z.object({
  buttonLabel: z.string().optional(),
  cartInquiryButtonLabel: z.string().optional(),
  inquiryOnlyButtonLabel: z.string().optional(),
  buttonBackgroundColor: z.string().optional(),
  buttonHoverColor: z.string().optional(),
  buttonTextColor: z.string().optional(),
  availabilityLabel: z.string().optional(),
  detailNotice: z.string().optional(),
  onlyInquiryNotice: z.string().optional(),
  selectionHint: z.string().optional(),
})
export type LandingPageProductInquirySettings = z.infer<typeof landingPageProductInquirySettingsSchema>

export const landingPageProductInquiryDefaults: Required<LandingPageProductInquirySettings> = {
  buttonLabel: 'Angebot anfordern',
  cartInquiryButtonLabel: 'Angebot anfordern',
  inquiryOnlyButtonLabel: 'Angebot anfordern',
  buttonBackgroundColor: '#fba466',
  buttonHoverColor: '#f39355',
  buttonTextColor: '#2f2d27',
  availabilityLabel: 'Nur auf Anfrage',
  detailNotice: 'Dieser Artikel ist nur auf Anfrage erhältlich. Der Preis bleibt sichtbar, die Bestellung erfolgt aber nicht direkt online.',
  onlyInquiryNotice: 'Dieser Artikel kann aktuell nicht direkt online bestellt werden. Nutzen Sie stattdessen die Anfrage.',
  selectionHint: 'Bitte wählen Sie erst alle Varianten aus, bevor Sie die Anfrage senden.',
}

export const landingPageAboutPageStatSchema = z.object({
  value: z.string(),
  label: z.string(),
})
export type LandingPageAboutPageStat = z.infer<typeof landingPageAboutPageStatSchema>

export const landingPageAboutPageFeatureSchema = z.object({
  title: z.string(),
  body: z.string(),
})
export type LandingPageAboutPageFeature = z.infer<typeof landingPageAboutPageFeatureSchema>

export const landingPageAboutPageSettingsSchema = z.object({
  enabled: z.boolean().optional(),
  metaTitle: z.string().optional(),
  metaDescription: z.string().optional(),
  heroEyebrow: z.string().optional(),
  heroTitle: z.string().optional(),
  heroBody: z.string().optional(),
  heroImageSrc: z.string().optional(),
  heroImageAlt: z.string().optional(),
  heroMobileImageSrc: z.string().optional(),
  heroMobileImageAlt: z.string().optional(),
  heroCta: menuPageBuilderButtonSchema.optional(),
  heroSecondaryCta: menuPageBuilderButtonSchema.optional(),
  introTitle: z.string().optional(),
  introBodyHtml: z.string().optional(),
  stats: z.array(landingPageAboutPageStatSchema).optional(),
  featureTitle: z.string().optional(),
  featureSubtitle: z.string().optional(),
  features: z.array(landingPageAboutPageFeatureSchema).optional(),
  storyTitle: z.string().optional(),
  storyBodyHtml: z.string().optional(),
  storyImageSrc: z.string().optional(),
  storyImageAlt: z.string().optional(),
  contactTitle: z.string().optional(),
  contactBody: z.string().optional(),
  contactCta: menuPageBuilderButtonSchema.optional(),
})
export type LandingPageAboutPageSettings = z.infer<typeof landingPageAboutPageSettingsSchema>

export const landingPageShopSettingsSchema = z.object({
  title: z.string().optional(),
  description: z.string().optional(),
  metaTitle: z.string().optional(),
  metaDescription: z.string().optional(),
  metaKeywords: z.string().optional(),
  showAllBrands: z.boolean().optional(),
  productInquiry: landingPageProductInquirySettingsSchema.optional(),
  aboutPage: landingPageAboutPageSettingsSchema.optional(),
  pageBuilder: menuPageBuilderSchema.optional(),
})
export type LandingPageShopSettings = z.infer<typeof landingPageShopSettingsSchema>

export const landingPageTemplateSchema = z.object({
  id: z.string(),
  name: z.string(),
  values: landingPageShopSettingsSchema,
  created: z.date(),
  changed: z.date(),
})
export type LandingPageTemplate = z.infer<typeof landingPageTemplateSchema>

export const landingPageSettingsShopsSchema = z.object({
  '101': landingPageShopSettingsSchema.optional(),
  '201': landingPageShopSettingsSchema.optional(),
  '202': landingPageShopSettingsSchema.optional(),
  '203': landingPageShopSettingsSchema.optional(),
  '204': landingPageShopSettingsSchema.optional(),
  '205': landingPageShopSettingsSchema.optional(),
})
export type LandingPageSettingsShops = z.infer<typeof landingPageSettingsShopsSchema>

export const landingPageSettingsSchema = z.object({
  key: z.string(),
  shops: landingPageSettingsShopsSchema,
  presets: z.array(landingPageTemplateSchema).optional(),
  created: z.date(),
  changed: z.date(),
})
export type LandingPageSettings = z.infer<typeof landingPageSettingsSchema>

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const sanitizeString = (value: unknown) => {
  if (typeof value !== 'string') return undefined
  const sanitized = value.trim()
  return sanitized || undefined
}

const sanitizeHexColor = (value: unknown) => {
  const sanitized = sanitizeString(value)
  return sanitized && /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(sanitized)
    ? sanitized
    : undefined
}

const sanitizeBoolean = (value: unknown) =>
  typeof value === 'boolean' ? value : undefined

const hasDefinedValue = (value: Record<string, unknown>) =>
  Object.values(value).some(entry => entry !== undefined)

const stripUndefinedObject = <T extends Record<string, unknown>>(value: T) =>
  Object.entries(value).reduce((acc, [key, entry]) => {
    if (entry !== undefined) acc[key as keyof T] = entry as T[keyof T]
    return acc
  }, {} as T)

const sanitizeDate = (value: unknown, fallback: Date) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value

  const parsed = new Date(String(value || ''))
  return Number.isNaN(parsed.getTime()) ? fallback : parsed
}

const toSixDigitHex = (value: string) =>
  value.length === 4
    ? `#${value.slice(1).split('').map(char => `${char}${char}`).join('')}`
    : value

const darkenHexColor = (value: string, ratio = 0.08) => {
  const sanitized = sanitizeHexColor(value)
  if (!sanitized) return undefined

  const hex = toSixDigitHex(sanitized).slice(1)
  const amount = Math.min(Math.max(ratio, 0), 1)

  const next = [0, 2, 4]
    .map(index => parseInt(hex.slice(index, index + 2), 16))
    .map(channel => Math.max(0, Math.min(255, Math.round(channel * (1 - amount)))))
    .map(channel => channel.toString(16).padStart(2, '0'))
    .join('')

  return `#${next}`
}

export const sanitizeLandingPageProductInquirySettings = (
  value: unknown
): LandingPageProductInquirySettings | undefined => {
  if (!isRecord(value)) return undefined

  const settings: LandingPageProductInquirySettings = {
    buttonLabel: sanitizeString(value.buttonLabel),
    cartInquiryButtonLabel: sanitizeString(value.cartInquiryButtonLabel),
    inquiryOnlyButtonLabel: sanitizeString(value.inquiryOnlyButtonLabel),
    buttonBackgroundColor: sanitizeHexColor(value.buttonBackgroundColor),
    buttonHoverColor: sanitizeHexColor(value.buttonHoverColor),
    buttonTextColor: sanitizeHexColor(value.buttonTextColor),
    availabilityLabel: sanitizeString(value.availabilityLabel),
    detailNotice: sanitizeString(value.detailNotice),
    onlyInquiryNotice: sanitizeString(value.onlyInquiryNotice),
    selectionHint: sanitizeString(value.selectionHint),
  }

  const sanitizedSettings = stripUndefinedObject(settings)

  return hasDefinedValue(sanitizedSettings)
    ? sanitizedSettings
    : undefined
}

export const resolveLandingPageProductInquirySettings = (
  defaults?: LandingPageProductInquirySettings,
  overrides?: LandingPageProductInquirySettings
): Required<LandingPageProductInquirySettings> => {
  const safeDefaults = sanitizeLandingPageProductInquirySettings(defaults) || {}
  const safeOverrides = sanitizeLandingPageProductInquirySettings(overrides) || {}
  const defaultButtonBackgroundColor = safeDefaults.buttonBackgroundColor
    || landingPageProductInquiryDefaults.buttonBackgroundColor
  const buttonBackgroundColor = safeOverrides.buttonBackgroundColor
    || defaultButtonBackgroundColor

  return {
    buttonLabel: safeOverrides.buttonLabel || safeDefaults.buttonLabel || landingPageProductInquiryDefaults.buttonLabel,
    cartInquiryButtonLabel: safeOverrides.cartInquiryButtonLabel
      || safeOverrides.buttonLabel
      || safeDefaults.cartInquiryButtonLabel
      || safeDefaults.buttonLabel
      || landingPageProductInquiryDefaults.cartInquiryButtonLabel,
    inquiryOnlyButtonLabel: safeOverrides.inquiryOnlyButtonLabel
      || safeOverrides.buttonLabel
      || safeDefaults.inquiryOnlyButtonLabel
      || safeDefaults.buttonLabel
      || landingPageProductInquiryDefaults.inquiryOnlyButtonLabel,
    buttonBackgroundColor,
    buttonHoverColor: safeOverrides.buttonHoverColor
      || (safeOverrides.buttonBackgroundColor
        ? darkenHexColor(buttonBackgroundColor, 0.08)
        : undefined)
      || safeDefaults.buttonHoverColor
      || darkenHexColor(defaultButtonBackgroundColor, 0.08)
      || landingPageProductInquiryDefaults.buttonHoverColor,
    buttonTextColor: safeOverrides.buttonTextColor
      || safeDefaults.buttonTextColor
      || landingPageProductInquiryDefaults.buttonTextColor,
    availabilityLabel: safeOverrides.availabilityLabel || safeDefaults.availabilityLabel || landingPageProductInquiryDefaults.availabilityLabel,
    detailNotice: safeOverrides.detailNotice || safeDefaults.detailNotice || landingPageProductInquiryDefaults.detailNotice,
    onlyInquiryNotice: safeOverrides.onlyInquiryNotice || safeDefaults.onlyInquiryNotice || landingPageProductInquiryDefaults.onlyInquiryNotice,
    selectionHint: safeOverrides.selectionHint || safeDefaults.selectionHint || landingPageProductInquiryDefaults.selectionHint,
  }
}

const sanitizeAboutPageButton = (value: unknown): MenuPageBuilderButton | undefined =>
  sanitizeMenuPageBuilderButton(value)

const sanitizeAboutPageStats = (value: unknown): LandingPageAboutPageStat[] | undefined => {
  if (!Array.isArray(value)) return undefined

  const stats = value.reduce<LandingPageAboutPageStat[]>((acc, entry) => {
    if (!isRecord(entry)) return acc

    const statValue = sanitizeString(entry.value)
    const label = sanitizeString(entry.label)
    if (!statValue || !label) return acc

    acc.push({ value: statValue, label })
    return acc
  }, [])

  return stats.length ? stats : undefined
}

const sanitizeAboutPageFeatures = (value: unknown): LandingPageAboutPageFeature[] | undefined => {
  if (!Array.isArray(value)) return undefined

  const features = value.reduce<LandingPageAboutPageFeature[]>((acc, entry) => {
    if (!isRecord(entry)) return acc

    const title = sanitizeString(entry.title)
    const body = sanitizeString(entry.body)
    if (!title || !body) return acc

    acc.push({ title, body })
    return acc
  }, [])

  return features.length ? features : undefined
}

export const sanitizeLandingPageAboutPageSettings = (
  value: unknown
): LandingPageAboutPageSettings | undefined => {
  if (!isRecord(value)) return undefined

  const settings: LandingPageAboutPageSettings = {
    enabled: sanitizeBoolean(value.enabled),
    metaTitle: sanitizeString(value.metaTitle),
    metaDescription: sanitizeString(value.metaDescription),
    heroEyebrow: sanitizeString(value.heroEyebrow),
    heroTitle: sanitizeString(value.heroTitle),
    heroBody: sanitizeString(value.heroBody),
    heroImageSrc: sanitizeString(value.heroImageSrc),
    heroImageAlt: sanitizeString(value.heroImageAlt),
    heroMobileImageSrc: sanitizeString(value.heroMobileImageSrc),
    heroMobileImageAlt: sanitizeString(value.heroMobileImageAlt),
    heroCta: sanitizeAboutPageButton(value.heroCta),
    heroSecondaryCta: sanitizeAboutPageButton(value.heroSecondaryCta),
    introTitle: sanitizeString(value.introTitle),
    introBodyHtml: sanitizeString(value.introBodyHtml),
    stats: sanitizeAboutPageStats(value.stats),
    featureTitle: sanitizeString(value.featureTitle),
    featureSubtitle: sanitizeString(value.featureSubtitle),
    features: sanitizeAboutPageFeatures(value.features),
    storyTitle: sanitizeString(value.storyTitle),
    storyBodyHtml: sanitizeString(value.storyBodyHtml),
    storyImageSrc: sanitizeString(value.storyImageSrc),
    storyImageAlt: sanitizeString(value.storyImageAlt),
    contactTitle: sanitizeString(value.contactTitle),
    contactBody: sanitizeString(value.contactBody),
    contactCta: sanitizeAboutPageButton(value.contactCta),
  }

  const sanitizedSettings = stripUndefinedObject(settings)

  return hasDefinedValue(sanitizedSettings)
    ? sanitizedSettings
    : undefined
}

export const sanitizeLandingPageShopSettings = (
  value: unknown
): LandingPageShopSettings | undefined => {
  if (!isRecord(value)) return undefined

  const settings: LandingPageShopSettings = {
    title: sanitizeString(value.title),
    description: sanitizeString(value.description),
    metaTitle: sanitizeString(value.metaTitle),
    metaDescription: sanitizeString(value.metaDescription),
    metaKeywords: sanitizeString(value.metaKeywords),
    showAllBrands: sanitizeBoolean(value.showAllBrands),
    productInquiry: sanitizeLandingPageProductInquirySettings(value.productInquiry),
    aboutPage: sanitizeLandingPageAboutPageSettings(value.aboutPage),
    pageBuilder: sanitizeMenuPageBuilder(value.pageBuilder),
  }

  const sanitizedSettings = stripUndefinedObject(settings)

  return hasDefinedValue(sanitizedSettings)
    ? sanitizedSettings
    : undefined
}

const sanitizeLandingPageTemplate = (
  value: unknown
): LandingPageTemplate | undefined => {
  if (!isRecord(value)) return undefined

  const id = sanitizeString(value.id)
  const name = sanitizeString(value.name)
  const values = sanitizeLandingPageShopSettings(value.values)

  if (!id || !name || !values) return undefined

  return {
    id,
    name,
    values,
    created: sanitizeDate(value.created, new Date(0)),
    changed: sanitizeDate(value.changed, new Date(0)),
  }
}

const emptyShops = (): LandingPageSettingsShops =>
  landingPageManagedShopIds.reduce((acc, shopId) => ({
    ...acc,
    [String(shopId)]: undefined,
  }), {} as LandingPageSettingsShops)

export const sanitizeLandingPageSettings = (
  value: unknown
): LandingPageSettings => {
  const record = isRecord(value) ? value : {}

  const shops = landingPageManagedShopIds.reduce((acc, shopId) => {
    const shopKey = String(shopId) as LandingPageManagedShopKey
    return {
      ...acc,
      [shopKey]: sanitizeLandingPageShopSettings(record.shops && isRecord(record.shops)
        ? record.shops[shopKey]
        : undefined),
    }
  }, emptyShops())

  return {
    key: sanitizeString(record.key) || 'default',
    shops,
    presets: Array.isArray(record.presets)
      ? record.presets
          .map(entry => sanitizeLandingPageTemplate(entry))
          .filter((entry): entry is LandingPageTemplate => !!entry)
      : [],
    created: sanitizeDate(record.created, new Date(0)),
    changed: sanitizeDate(record.changed, new Date(0)),
  }
}

export const getLandingPageShopSettings = (
  settings: Pick<LandingPageSettings, 'shops'> | undefined,
  shopId: number
): LandingPageShopSettings | undefined => {
  const shopKey = String(shopId) as LandingPageManagedShopKey
  return settings?.shops?.[shopKey]
}

export const applyLandingPageSiteChrome = (
  pageBuilder: MenuPageBuilder | undefined,
  sitePageBuilder: MenuPageBuilder | undefined
): MenuPageBuilder | undefined => {
  const safePageBuilder = sanitizeMenuPageBuilder(pageBuilder)
  const safeSitePageBuilder = sanitizeMenuPageBuilder(sitePageBuilder)

  if (!safePageBuilder) return undefined
  if (!safeSitePageBuilder) return safePageBuilder

  return sanitizeMenuPageBuilder({
    ...safePageBuilder,
    layout: safeSitePageBuilder.layout || safePageBuilder.layout,
    theme: safeSitePageBuilder.theme || safePageBuilder.theme,
    search: safeSitePageBuilder.search || safePageBuilder.search,
    products: safeSitePageBuilder.products || safePageBuilder.products,
    footer: safeSitePageBuilder.footer || safePageBuilder.footer,
  })
}
